import { useEffect, useRef } from 'react';

// Beat between the row scrolling into view + flashing and its CTA surface opening, so the member
// registers *which* task was deep-linked before the modal/drawer covers it.
export const DEEP_LINK_CTA_DELAY_MS = 600;

/**
 * When `enabled` at first mount (i.e. this task/group is the deep-link target), opens its CTA surface
 * after a short beat via `onOpen`. Captured once on mount and fired at most once — later `enabled`
 * changes are ignored, so clearing the deep-link params from the URL after consumption can't cancel a
 * pending open, and closing the surface won't re-trigger it. Mirrors useDeepLinkHighlight's
 * flash-on-mount semantics.
 */
export default function useDeepLinkAutoOpen(enabled: boolean, onOpen: () => void): void {
    const pendingRef = useRef(enabled);
    const onOpenRef = useRef(onOpen);
    // Synced in an effect (not during render) so a discarded concurrent render can't leak its
    // callback into the committed timer's ref.
    useEffect(() => {
        onOpenRef.current = onOpen;
    });

    useEffect(() => {
        if (!pendingRef.current) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            pendingRef.current = false;
            onOpenRef.current();
        }, DEEP_LINK_CTA_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, []);
}
